<template>
  <div class="p-6">
    <h3 class="text-lg font-semibold mb-6 dark:text-white">
      New GBP Campaign
    </h3>

    <div v-if="servicesLoading" class="mb-5 text-sm text-gray-500 dark:text-gray-400">
      Loading approved services...
    </div>
    <div
      v-else-if="approvedServices.length === 0"
      class="mb-5 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
      Add at least one approved service to this location or its business before generating posts. This prevents AI from inventing services.
    </div>
    <div v-else class="mb-5 rounded-md border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 px-4 py-3">
      <p class="text-xs font-medium text-green-800 dark:text-green-200 mb-2">
        Posts will be restricted to these {{ servicesInherited ? 'business' : 'location' }} services:
      </p>
      <div class="flex flex-wrap gap-2">
        <span v-for="service in approvedServices" :key="service.name" class="rounded bg-white dark:bg-gray-800 px-2 py-1 text-xs text-gray-700 dark:text-gray-200">
          {{ service.name }}
        </span>
      </div>
    </div>

    <UiControl id="campaign-name" label="Campaign Name">
      <UiInput v-model="form.name" :placeholder="defaultName" size="sm" />
    </UiControl>

    <div class="grid grid-cols-2 gap-4">
      <UiControl id="llm-provider" label="LLM Provider">
        <UiSelect
          id="llm-provider"
          v-model="form.llm_provider"
          :options="llmOptions"
          size="sm" />
      </UiControl>

      <UiControl v-if="form.image_source === 'ai_generated'" id="image-provider" label="AI Image Provider">
        <UiSelect
          id="image-provider"
          v-model="form.image_provider"
          :options="imageOptions"
          size="sm" />
      </UiControl>
    </div>

    <UiControl id="image-source" label="Post photos" hint="Use authentic company photos whenever possible; every image receives the same brand treatment.">
      <UiRadioGroup
        id="image-source"
        v-model="form.image_source"
        :options="imageSourceOptions"
        position="horizontal"
        size="sm" />
    </UiControl>

    <UiControl
      v-if="form.image_source === 'company_photos'"
      id="company-photos"
      label="Company photos"
      hint="Upload real photos from this business. They are rotated across posts, cleaned up, cropped, and branded automatically.">
      <input type="file" accept="image/*" multiple class="text-sm dark:text-gray-300" @change="onPhotosSelected">
      <div v-if="selectedPhotos.length" class="mt-2 flex flex-wrap gap-2">
        <span v-for="photo in selectedPhotos" :key="`${photo.name}-${photo.lastModified}`" class="rounded bg-gray-100 dark:bg-gray-700 px-2 py-1 text-xs dark:text-gray-200">
          {{ photo.name }}
        </span>
      </div>
      <p v-else class="mt-2 text-xs text-amber-700 dark:text-amber-300">
        Add at least one authentic company photo to use this option.
      </p>
    </UiControl>

    <div class="grid grid-cols-2 gap-4">
      <UiControl id="post-type" label="Post Type">
        <UiRadioGroup
          id="post-type"
          v-model="form.post_type"
          :options="postTypeOptions"
          position="horizontal"
          size="sm" />
      </UiControl>

      <UiControl id="cta-type" label="CTA">
        <UiRadioGroup
          id="cta-type"
          v-model="form.cta_type"
          :options="ctaOptions"
          position="horizontal"
          size="sm" />
      </UiControl>
    </div>

    <div class="grid grid-cols-3 gap-4">
      <UiControl id="date-start" label="Start Date">
        <UiInput v-model="form.date_range_start" type="date" size="sm" />
      </UiControl>

      <UiControl id="date-end" label="End Date">
        <UiInput v-model="form.date_range_end" type="date" size="sm" />
      </UiControl>

      <UiControl id="post-count" label="Number of Posts">
        <UiSelect
          id="post-count"
          v-model="form.target_posts_count"
          :options="postCountOptions"
          size="sm" />
      </UiControl>
    </div>

    <UiControl id="keywords" label="Keywords">
      <div class="flex flex-wrap gap-2 mb-2" v-if="form.keywords.length">
        <span
          v-for="(kw, i) in form.keywords"
          :key="i"
          class="bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1.5">
          {{ kw }}
          <button @click="removeKeyword(i)" class="text-primary-500 hover:text-red-500 text-sm leading-none">&times;</button>
        </span>
      </div>
      <div class="flex gap-2">
        <UiInput v-model="newKeyword" placeholder="Add keyword..." size="sm" class="flex-1" @keydown.enter.prevent="addKeyword" />
        <button @click="addKeyword" class="btn btn-secondary btn-sm">
          Add
        </button>
      </div>
      <p v-if="form.keywords.length && effectiveCount > form.keywords.length" class="text-xs text-gray-500 mt-1">
        {{ form.keywords.length }} keywords will cycle across {{ effectiveCount }} posts
      </p>
    </UiControl>

    <details class="mb-5" open>
      <summary class="text-sm font-medium cursor-pointer dark:text-gray-300 text-gray-700 select-none">
        Image Overlay
      </summary>
      <div class="grid grid-cols-2 gap-4 mt-3">
        <UiControl id="bar-color" label="Bar Color" hint="Overrides business brand color">
          <div class="flex gap-2 items-center">
            <input v-model="form.bar_color" type="color" class="w-10 h-10 rounded cursor-pointer border-2 border-gray-200 dark:border-gray-600">
            <UiInput v-model="form.bar_color" placeholder="Business default" size="sm" />
          </div>
        </UiControl>
        <UiControl id="bar-font" label="Bar Font" hint="Overrides business brand font">
          <UiSelect
            id="bar-font"
            v-model="form.bar_font"
            :options="fontOptions"
            size="sm" />
        </UiControl>
      </div>
    </details>

    <details class="mb-5">
      <summary class="text-sm font-medium cursor-pointer dark:text-gray-300 text-gray-700 select-none">
        UTM Parameters
      </summary>
      <div class="grid grid-cols-3 gap-3 mt-3">
        <UiControl id="utm-source" label="Source">
          <UiInput v-model="form.utm_params.source" size="sm" />
        </UiControl>
        <UiControl id="utm-medium" label="Medium">
          <UiInput v-model="form.utm_params.medium" size="sm" />
        </UiControl>
        <UiControl id="utm-campaign" label="Campaign">
          <UiInput v-model="form.utm_params.campaign" size="sm" />
        </UiControl>
      </div>
    </details>

    <UiControl id="custom-instructions" label="Custom Instructions" hint="Optional tone or style guidance for the LLM">
      <UiTextarea v-model="form.custom_instructions" size="sm" placeholder="e.g. Use a professional tone, emphasize emergency services..." />
    </UiControl>

    <div class="flex justify-end gap-3 mt-2 pt-4 border-t dark:border-gray-700">
      <button @click="$emit('on-close-panel')" class="btn btn-secondary btn-sm">
        Cancel
      </button>
      <button @click="submit" :disabled="submitting || servicesLoading || approvedServices.length === 0" class="btn btn-primary btn-sm">
        {{ submitting ? 'Creating...' : `Generate ${effectiveCount} Posts` }}
      </button>
    </div>

    <p v-if="error" class="text-red-500 text-sm mt-3">
      {{ error }}
    </p>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { createGbpCampaign } from '~/javascript/api/gbp_campaigns'
import { getLocation } from '~/javascript/api/locations'
import UiInput from '~/javascript/components/ui/form/input/input.vue'
import UiTextarea from '~/javascript/components/ui/form/textarea/textarea.vue'
import UiSelect from '~/javascript/components/ui/form/select/select.vue'
import UiRadioGroup from '~/javascript/components/ui/form/radio/radio_group.vue'
import UiControl from '~/javascript/components/ui/form/control/control.vue'

const props = defineProps({
  locationId: { type: String, default: '' },
  defaultKeywords: { type: Array, default: () => []}
})
const emit = defineEmits(['created', 'on-close-panel'])

const today = new Date().toISOString().split('T')[0]
const nextMonth = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0]
const defaultName = `${new Date().toLocaleString('default', { month: 'long', year: 'numeric' })} Posts`

const llmOptions = [
  { id: 'anthropic', title: 'Anthropic (Claude)' },
  { id: 'openai', title: 'OpenAI (GPT-4o)' },
  { id: 'gemini', title: 'Gemini' }
]

const imageOptions = [
  { id: 'gemini_images', title: 'Gemini Images' },
  { id: 'banana', title: 'Banana' }
]

const imageSourceOptions = [
  { value: 'company_photos', label: 'Company photos' },
  { value: 'ai_generated', label: 'AI-generated' }
]

const postTypeOptions = [
  { value: 'update', label: 'Update' },
  { value: 'event', label: 'Event' }
]

const ctaOptions = [
  { value: 'call_now', label: 'Call Now' },
  { value: 'learn_more', label: 'Learn More' }
]

const fontOptions = [
  { id: '', title: 'Business Default' },
  { id: 'DejaVu-Sans', title: 'DejaVu Sans' },
  { id: 'DejaVu-Sans-Bold', title: 'DejaVu Sans Bold' },
  { id: 'DejaVu-Serif', title: 'DejaVu Serif' },
  { id: 'DejaVu-Serif-Bold', title: 'DejaVu Serif Bold' },
  { id: 'Lato-Regular', title: 'Lato' },
  { id: 'Lato-Bold', title: 'Lato Bold' },
  { id: 'Lato-Light', title: 'Lato Light' },
  { id: 'Nimbus-Sans-Regular', title: 'Nimbus Sans' },
  { id: 'Nimbus-Sans-Bold', title: 'Nimbus Sans Bold' },
  { id: 'Roboto', title: 'Roboto' },
  { id: 'Roboto-Bold', title: 'Roboto Bold' },
  { id: 'Roboto-Condensed', title: 'Roboto Condensed' },
  { id: 'Roboto-Light', title: 'Roboto Light' }
]

const postCountOptions = [
  { id: 0, title: 'Auto (1 per keyword)' },
  { id: 5, title: '5 posts' },
  { id: 10, title: '10 posts' },
  { id: 15, title: '15 posts' },
  { id: 20, title: '20 posts' },
  { id: 30, title: '30 posts' }
]

const form = reactive({
  name: defaultName,
  post_type: 'update',
  cta_type: 'learn_more',
  llm_provider: 'anthropic',
  image_provider: 'gemini_images',
  image_source: 'company_photos',
  target_posts_count: 0,
  keywords: [...(props.defaultKeywords || [])],
  date_range_start: today,
  date_range_end: nextMonth,
  bar_color: '',
  bar_font: '',
  utm_params: { source: 'google', medium: 'organic', campaign: 'gbp-posts' },
  custom_instructions: ''
})

const effectiveCount = computed(() => {
  const target = parseInt(form.target_posts_count, 10) || 0
  return target > 0 ? target : form.keywords.length
})

const newKeyword = ref('')
const submitting = ref(false)
const error = ref(null)
const servicesLoading = ref(true)
const approvedServices = ref([])
const servicesInherited = ref(false)
const selectedPhotos = ref([])

function addKeyword() {
  const kw = newKeyword.value.trim()
  if (kw && !form.keywords.includes(kw)) {
    form.keywords.push(kw)
    newKeyword.value = ''
  }
}

function removeKeyword(i) {
  form.keywords.splice(i, 1)
}

function onPhotosSelected(event) {
  selectedPhotos.value = Array.from(event.target.files || [])
}

function campaignFormData() {
  const data = new FormData()
  const append = (key, value) => data.append(`gbp_campaign[${key}]`, value ?? '')

  const campaignFields = [
    'name', 'post_type', 'cta_type', 'llm_provider', 'image_provider', 'image_source',
    'date_range_start', 'date_range_end', 'custom_instructions', 'target_posts_count',
    'bar_color', 'bar_font'
  ]
  campaignFields.forEach((key) => append(key, form[key]))
  form.keywords.forEach((keyword) => data.append('gbp_campaign[keywords][]', keyword))
  Object.entries(form.utm_params).forEach(([key, value]) => {
    data.append(`gbp_campaign[utm_params][${key}]`, value ?? '')
  })
  selectedPhotos.value.forEach((photo) => data.append('gbp_campaign[source_photos][]', photo))

  return data
}

async function submit() {
  if (approvedServices.value.length === 0) {
    error.value = 'Add approved services before creating this campaign'
    return
  }
  if (form.keywords.length === 0) {
    error.value = 'Add at least one keyword'
    return
  }
  if (form.image_source === 'company_photos' && selectedPhotos.value.length === 0) {
    error.value = 'Upload at least one authentic company photo, or switch to AI-generated images'
    return
  }
  submitting.value = true
  error.value = null
  try {
    const { data } = await createGbpCampaign(props.locationId, campaignFormData())
    emit('created', data)
  } catch (e) {
    error.value = e.response?.data?.error || 'Failed to create campaign'
  } finally {
    submitting.value = false
  }
}

getLocation(props.locationId).then(({ data }) => {
  approvedServices.value = data.effective_services || []
  servicesInherited.value = Boolean(data.services_inherited)
}).catch(() => {
  error.value = 'Unable to load approved services for this location'
}).finally(() => {
  servicesLoading.value = false
})
</script>
